import { getCurrentLang, handleHtmlTags } from "@platformx/utilities";
import { buildContentUrl, omitEmpty } from "./common";
import { getPublisherNode } from "./publisher";

export type QuizStructureDataInput = {
  title?: string;
  description?: string;
  /** Questions as hydrated into quiz state: `{ question, options_compound_fields }`. */
  questions?: any[];
  /** Slug (preferred) or page id used in the delivery route. */
  pageUrl?: string;
  selectedBrandRoute?: string;
  language?: string;
};

const buildAnswer = (option: any, position: number) => ({
  "@type": "Answer",
  text: handleHtmlTags(option?.option_text || ""),
  position,
});

/**
 * A question's `acceptedAnswer`: an object for single-answer questions, an
 * ARRAY for multi-select ones (`question_type: "Multiple"` allows several
 * options flagged `is_correct`). Omitted when the author has flagged none.
 */
const buildAcceptedAnswer = (options: any[]) => {
  const correct = options
    .map((option, position) => ({ option, position }))
    .filter(({ option }) => Boolean(option?.is_correct));

  if (correct.length === 0) return undefined;
  if (correct.length === 1) return buildAnswer(correct[0].option, correct[0].position);
  return correct.map(({ option, position }) => buildAnswer(option, position));
};

/**
 * `Quiz` markup (KIWI-6285).
 *
 * Corrects `Question.name` → `text` (the schema.org property) and adds the
 * `acceptedAnswer` / `eduQuestionType` / `learningResourceType` / answer
 * `position` fields Google needs to render a quiz result, plus page-level
 * `@id` / `url` / `inLanguage` / `publisher`.
 *
 * `about`, `educationalLevel` and `assesses` appear in the ticket's sample but
 * have no authoring-time source field, so they are omitted rather than faked.
 */
export const buildQuizStructureData = ({
  title = "",
  description = "",
  questions = [],
  pageUrl = "",
  selectedBrandRoute = "en",
  language = "",
}: QuizStructureDataInput = {}) => {
  const url = buildContentUrl("quiz", pageUrl, selectedBrandRoute);

  const hasPart = (questions || [])
    .filter(Boolean)
    .map((question: any) => {
      // A blank option would otherwise emit {"@type":"Answer","text":"","position":n}
      // — a node carrying only its @type, which the foundation's omit-when-empty
      // rule forbids. The Poll builder already drops blanks the same way.
      const options = (question?.options_compound_fields || [])
        .filter(Boolean)
        .filter((option: any) => Boolean(handleHtmlTags(option?.option_text || "")));
      return omitEmpty({
        "@type": "Question",
        eduQuestionType: "Multiple choice",
        learningResourceType: "Practice problem",
        text: handleHtmlTags(question?.question || ""),
        acceptedAnswer: buildAcceptedAnswer(options),
        suggestedAnswer: options.map(buildAnswer),
      });
    })
    // schema.org requires a Question to carry its text. Counting keys let a
    // question with no text but a few blank options through, because the blank
    // suggestedAnswer array pushed it past the threshold (KIWI-6280).
    .filter((question: any) => Boolean(question?.text));

  return omitEmpty({
    "@context": "https://schema.org",
    "@type": "Quiz",
    "@id": url ? `${url}#quiz` : "",
    name: handleHtmlTags(title),
    description: handleHtmlTags(description),
    url,
    inLanguage: language || getCurrentLang(),
    publisher: getPublisherNode(),
    hasPart,
  });
};
